import {EVENT_EXPIRED, OktaAuth, toRelativeUrl} from '@okta/okta-auth-js';

import {Mocks} from 'config/mocks';
import {getRoute} from 'config/navigation';
import {StaticConfig} from 'config/staticConfig';
import {clearGenesysAuthLocalStorageKeys} from 'features/ChatBot/utils/authenticatedChat';
import {history} from 'shared/Router/utils';
import {APP_LOCAL_STORAGE_OKTA_TOKEN_STORAGE} from 'shared/Storage/constants/localStorageKeys';
import {clearSessionData, MINUTE_MS, TIMEOUT_WARNING_MINUTES} from 'shared/utils/session';
import getStore from 'store';
import {BypassAuth} from 'utils/bypass-auth';
import {getMobileAccessToken, getMobileIdToken} from 'utils/MobileBridge';

const scopes: string[] = ['openid', 'profile', 'email', 'offline_access'];

// Override okta's default auth callback to use a different route for authentication
export const onAuthRequired = () => {
  const params = window.location.search;
  history.push(`/customers${getRoute('/login')}${params}`);
};

export const restoreOriginalUri = async (_: any, originalUri: string) => {
  if (!!originalUri && originalUri.includes('/customers/')) {
    const startIndexSubRoute = originalUri.indexOf('/customers/');
    const newURL = originalUri.substring(startIndexSubRoute, startIndexSubRoute + originalUri.length);
    history.replace(toRelativeUrl(newURL, window.location.origin));
  } else {
    const uri = originalUri && originalUri.includes('/customers/implicit') ? '/customers' : originalUri;
    history.replace(toRelativeUrl(uri || '/customers', window.location.origin));
  }
};

const oktaConfig = {
  tokenManager: {
    autoRenew: true
  },
  history,
  issuer: StaticConfig.oktaIssuer,
  redirectUri: `${window.location.origin}/customers${getRoute('/implicit/callback')}`,
  clientId: StaticConfig.oktaClientId,
  scopes,
  maxClockSkew: 900
};

const oktaClient = Mocks.Authentication ? new BypassAuth(oktaConfig) : new OktaAuth(oktaConfig);

oktaClient.tokenManager.on(EVENT_EXPIRED, async () => {
  const lastActivityTimestamp = getStore().getState()?.sessionActivity?.lastActivityTimestamp;
  const currentDate = Date.now();
  const fifteenMinutesAgo = currentDate - TIMEOUT_WARNING_MINUTES * MINUTE_MS;
  if (lastActivityTimestamp > fifteenMinutesAgo && lastActivityTimestamp <= currentDate) {
    await forceRenewAccessToken();
  }
});

export const forceRenewAccessToken = async () =>
  oktaClient.tokenManager
    .renew('accessToken')
    .then((token) => token)
    .catch(() => {
      logout();
      return null;
    });

export const getAccessToken = async () => {
  if (!StaticConfig.mobileApplicationLoader) {
    const accessToken = await oktaClient.getOrRenewAccessToken();

    if (!accessToken) {
      logout();
      return null;
    }

    const accessTokenFromStorage = JSON.parse(
      window.localStorage.getItem(APP_LOCAL_STORAGE_OKTA_TOKEN_STORAGE) || '{}'
    )?.accessToken;
    const hasTokenExpired = !accessTokenFromStorage || oktaClient.tokenManager.hasExpired(accessTokenFromStorage);

    if (hasTokenExpired) {
      logout();
      return null;
    }

    return accessToken;
  }

  return getMobileAccessToken();
};

export const getIdToken = () => (!StaticConfig.mobileApplicationLoader ? oktaClient.getIdToken() : getMobileIdToken());

export const logout = async (logoutRedirectLink?: string) => {
  const logoutRedirect = logoutRedirectLink || StaticConfig.postLogoutURL;
  try {
    const Genesys = (window as any).Genesys;
    if (Genesys) {
      Genesys('command', 'Auth.logout');
      clearGenesysAuthLocalStorageKeys();
    }
    await oktaClient.signOut({postLogoutRedirectUri: logoutRedirect});
  } catch (e) {
    // If the Okta logout request fails, manually clear token storage and redirect.
    // 404 "Not found: Resource not found: me (Session)" errors were intermittently
    // seen when attempting to log out the Amica tab when opening a new tab to
    // Safelite (WEB-2156).
    // It's not a perfect logout function, but it's better than a crash.

    window.location.replace(logoutRedirect);
  } finally {
    clearSessionData();
  }
};

export default oktaClient;
